import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ContentGuidelines: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/help')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('help.backToHelp', 'Back to Help Center')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          FOR CREATORS
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Video Guidelines and Policies
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          IndeeClips is committed to providing a safe, respectful, and creative platform for all users. Our content guidelines ensure that the platform remains welcoming for creators and viewers of all ages while protecting freedom of expression within reasonable boundaries.
        </Typography>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Prohibited Content
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          The following types of content are strictly prohibited on IndeeClips:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Violent or graphic content:</strong> Excessive violence, gore, or content intended to shock or disgust viewers
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Hate speech:</strong> Content promoting hatred or violence against individuals or groups based on race, ethnicity, religion, disability, gender, age, or sexual orientation
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Sexual content:</strong> Pornography, sexually explicit material, or content exploiting minors in any way
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Harmful or dangerous acts:</strong> Content encouraging illegal activities, self-harm, or dangerous challenges that could cause injury
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Harassment and cyberbullying:</strong> Content intended to humiliate, threaten, or intimidate individuals
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            <strong>Spam and misleading content:</strong> Deceptive metadata, clickbait, scams, or repetitive content that manipulates platform features
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Copyright and Intellectual Property
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Only upload content you created or have permission to use
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Do not upload content containing copyrighted music, video clips, or images without proper licensing or fair use justification
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            Give proper credit when using Creative Commons or royalty-free content
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Respect DMCA takedown requests and our copyright policy (available on our DMCA page)
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Community Standards
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Be respectful:</strong> Treat other creators and viewers with respect, even when disagreeing
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Accurate metadata:</strong> Use titles, descriptions, and tags that accurately represent your content
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Age-appropriate content:</strong> Clearly mark mature content and avoid uploading content inappropriate for minors
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
            <strong>Cultural sensitivity:</strong> Be mindful of diverse audiences and avoid content that could be culturally insensitive, especially given India's diverse communities
          </Typography>
          <Typography component="li" variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            <strong>Authenticity:</strong> Avoid impersonating other creators, celebrities, or brands
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Enforcement and Appeals
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Content that violates our guidelines may be removed without notice. Repeated violations can result in channel suspension or permanent account termination. We use a combination of automated systems and human review to enforce these policies. If you believe your content was removed in error, you can appeal the decision by contacting our support team with details about the specific video and why you believe it complies with our guidelines.
        </Typography>

        <Typography variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
          Reporting Violations
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          If you encounter content that violates these guidelines, please report it using the "Report" button available on every video. Our moderation team reviews all reports and takes appropriate action. Your reports help keep IndeeClips safe for everyone.
        </Typography>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Questions About Our Guidelines?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            If you're unsure whether your content complies with our guidelines or have questions about specific policies, please reach out to our support team. Email{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ContentGuidelines;
